import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { MagnifyingGlassIcon, ChevronDownIcon, ClockIcon } from '@heroicons/react/24/outline';
import { applyGlMask, glMaskTail, padGlToTemplate } from './InvoiceEditForm';

export interface GLResult {
  account_number: string;
  description: string | null;
  asn: string | null;
  is_active: boolean | null;
  match_score?: number;
  uses?: number;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  onPick?: (gl: GLResult) => void;

  /**
   * Fires when the clerk has committed a GL — either picked one from the
   * dropdown or blurred out of the input. Unlike onChange (fires per keystroke),
   * this is the "I'm done editing this cell" signal that upstream logic
   * (e.g. cascade to remaining line items) should hook into.
   * The value passed is post-zero-pad, so upstream doesn't need to re-pad.
   */
  onCommit?: (value: string) => void;

  vendorNumber?: string;
  vendorName?: string;

  glFormatExample?: string;
  glFormatRegex?: string;
  glFormatPlaceholder?: string;
  glFormatErrorLabel?: string;

  disabled?: boolean;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || '';
const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * GL account combobox for line item entry.
 *
 * Behavior:
 *   1. Renders as a text input with a chevron. Clicking anywhere on the
 *      input opens a dropdown below.
 *   2. If a `vendorNumber` is provided, the top of the dropdown shows the
 *      GLs most-frequently used with that vendor at the current account
 *      (from historical invoices). One click populates the field.
 *   3. Below the frequency section is a search input that queries the
 *      full account chart (same behavior as the previous GLSearchField).
 *   4. Typing directly into the text input still works and is masked per
 *      the tenant's `gl_format_example` (auto-hyphen from AIPM-712).
 *
 * Replaces the previous split UI where the GL input and the search field
 * were separate widgets stacked on top of each other.
 */
const GLPicker: React.FC<Props> = ({
  value,
  onChange,
  onPick,
  onCommit,
  vendorNumber,
  vendorName,
  glFormatExample,
  glFormatRegex,
  glFormatPlaceholder,
  glFormatErrorLabel,
  disabled = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [open, setOpen] = useState(false);
  const [vendorFreq, setVendorFreq] = useState<GLResult[]>([]);
  const [vendorFreqLoading, setVendorFreqLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<GLResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Fetch vendor-frequency list whenever the vendor changes or the dropdown
  // opens. Cached across open/close via state.
  // Sends both vendor_number and vendor_name so the endpoint can match by
  // whichever is available. Necessary because many tenants (Cleveland,
  // Liberty, Jackson) have vendor rows without a vendor_number set — the
  // vendor was matched by name only, so we have to key on name too.
  const fetchVendorFreq = useCallback(async () => {
    if (!vendorNumber && !vendorName) {
      setVendorFreq([]);
      return;
    }
    setVendorFreqLoading(true);
    try {
      const params: Record<string, string | number> = { limit: 5 };
      if (vendorNumber) params.vendor_number = vendorNumber;
      if (vendorName) params.vendor_name = vendorName;
      const res = await axios.get(`${API_BASE_URL}/gl/for-vendor`, {
        params,
        headers: authHeaders(),
      });
      setVendorFreq(res.data?.results || []);
    } catch {
      setVendorFreq([]);
    } finally {
      setVendorFreqLoading(false);
    }
  }, [vendorNumber, vendorName]);

  useEffect(() => {
    if (open) fetchVendorFreq();
  }, [open, fetchVendorFreq]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = searchQuery.trim();
    if (trimmed.length < 2) {
      setSearchResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await axios.get(`${API_BASE_URL}/gl/search`, {
          params: { q: trimmed, limit: 15 },
          headers: authHeaders(),
        });
        setSearchResults(res.data?.results || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery]);

  // Click-outside closes
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handlePick = (gl: GLResult) => {
    onChange(gl.account_number);
    if (onPick) onPick(gl);
    if (onCommit) onCommit(gl.account_number);
    setSearchQuery('');
    setSearchResults([]);
    setOpen(false);
  };

  const handleBlur = () => {
    // Zero-pad partial entries to the tenant's full template length so the
    // clerk doesn't have to type trailing zeros. `010-441-5` -> `010-441-5-000-00`.
    // No-op if the account has no template configured or the field is empty.
    const padded = padGlToTemplate(value || '', glFormatExample);
    if (padded !== value) onChange(padded);
    if (onCommit) onCommit(padded);
  };

  // Validation border color — same regex logic as the previous input
  const trimmed = (value || '').trim();
  const glRegex = glFormatRegex ? new RegExp(glFormatRegex) : /-/;
  const glValid = !trimmed || glRegex.test(trimmed);
  const placeholder = glFormatPlaceholder
    ? `e.g. ${glFormatExample || glFormatPlaceholder}`
    : 'e.g. 010-441-5-250-00';

  // Phone-number-style mask: when the account has a gl_format_example, show
  // the untyped portion of the template as a faded suffix inside the input,
  // so the clerk always sees the full shape (e.g. `010-___-_-___-__`).
  // The underlay and input share the same padding + monospace font so the
  // tail visually continues right after the last typed character.
  const currentValue = value || '';
  const ghostTail = glFormatExample ? glMaskTail(currentValue, glFormatExample) : '';

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        {ghostTail && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 left-0 right-8 pl-3 py-2 text-sm font-mono flex items-center whitespace-pre"
          >
            <span className="invisible">{currentValue}</span>
            <span className="text-gray-400 dark:text-gray-500">{ghostTail}</span>
          </div>
        )}
        <input
          type="text"
          value={currentValue}
          onChange={(e) => onChange(applyGlMask(e.target.value, glFormatExample))}
          onBlur={handleBlur}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          placeholder={ghostTail ? '' : placeholder}
          maxLength={45}
          inputMode={glFormatExample ? 'numeric' : 'text'}
          disabled={disabled}
          autoComplete="off"
          className={`w-full pl-3 pr-8 py-2 text-sm font-mono border rounded-md bg-transparent
            focus:ring-2 focus:border-brand-500
            dark:text-white dark:placeholder-gray-400 relative z-30 ${
              glValid
                ? 'border-gray-300 dark:border-gray-600 focus:ring-brand-500'
                : 'border-red-400 focus:ring-red-500'
            }`}
        />
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="absolute right-1 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 z-40"
          tabIndex={-1}
          aria-label="Toggle GL picker"
        >
          <ChevronDownIcon className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {!glValid && (
        <p className="text-xs text-red-500 mt-1">
          {glFormatErrorLabel ||
            'GL ID usually has dashes (e.g. 010-441-5-250-00). A plain number is probably an item number, not a GL.'}
        </p>
      )}

      {open && (
        <div className="absolute z-50 mt-1 w-full max-h-96 overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg">
          {/* Vendor-frequency section */}
          {(vendorNumber || vendorName) && (
            <div>
              <div className="px-3 py-1.5 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-100 dark:border-gray-700 flex items-center gap-1.5">
                <ClockIcon className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" />
                <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Frequently used{vendorName ? ` with ${vendorName}` : ' with this vendor'}
                </span>
              </div>
              {vendorFreqLoading && (
                <div className="px-3 py-2 text-xs text-gray-500">Loading past usage…</div>
              )}
              {!vendorFreqLoading && vendorFreq.length === 0 && (
                <div className="px-3 py-2 text-xs text-gray-500">
                  No prior GLs for this vendor at this account yet.
                </div>
              )}
              {!vendorFreqLoading &&
                vendorFreq.map((g) => (
                  <button
                    key={`freq-${g.account_number}`}
                    type="button"
                    onClick={() => handlePick(g)}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-gray-900 dark:text-gray-100">
                        {g.account_number}
                        {g.asn && (
                          <span className="ml-2 px-1 py-0.5 text-[10px] bg-gray-200 dark:bg-gray-600 rounded">
                            {g.asn}
                          </span>
                        )}
                      </span>
                      <span className="text-[10px] text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        used {g.uses}×
                      </span>
                    </div>
                    {g.description && (
                      <div className="text-gray-500 dark:text-gray-400 mt-0.5">
                        {g.description}
                      </div>
                    )}
                  </button>
                ))}
            </div>
          )}

          {/* Search section */}
          <div>
            <div className="px-3 py-1.5 bg-gray-50 dark:bg-gray-700/50 border-b border-t border-gray-100 dark:border-gray-700 flex items-center gap-1.5">
              <MagnifyingGlassIcon className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" />
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Search all GLs
              </span>
            </div>
            <div className="p-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Type at least 2 characters…"
                autoFocus={!vendorNumber && !vendorName}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md
                  focus:ring-2 focus:ring-brand-500 focus:border-brand-500
                  dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                autoComplete="off"
              />
            </div>
            {searchLoading && (
              <div className="px-3 py-2 text-xs text-gray-500">Searching…</div>
            )}
            {!searchLoading && searchQuery.trim().length >= 2 && searchResults.length === 0 && (
              <div className="px-3 py-2 text-xs text-gray-500">
                No GL accounts match "{searchQuery.trim()}"
              </div>
            )}
            {!searchLoading &&
              searchResults.map((r) => (
                <button
                  key={`search-${r.account_number}`}
                  type="button"
                  onClick={() => handlePick(r)}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                >
                  <div className="font-mono text-gray-900 dark:text-gray-100">
                    {r.account_number}
                    {r.asn && (
                      <span className="ml-2 px-1 py-0.5 text-[10px] bg-gray-200 dark:bg-gray-600 rounded">
                        {r.asn}
                      </span>
                    )}
                    {r.is_active === false && (
                      <span className="ml-2 px-1 py-0.5 text-[10px] bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 rounded">
                        inactive
                      </span>
                    )}
                  </div>
                  {r.description && (
                    <div className="text-gray-500 dark:text-gray-400 mt-0.5">
                      {r.description}
                    </div>
                  )}
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GLPicker;
